<template>
    <div class="profile">
        <div class="user">
            <div class="user__avatar">
                <common-rating-avatar :user="physician" :size="avatarSize"></common-rating-avatar>
            </div>

            <div class="visit-layout">
                <h1 class="main-head">{{ physician.firstName }} {{ physician.lastName }}</h1>
                <p v-if="physician.lastReferralCreatedDateTime" class="visit">
                    {{ $t('modals.member_card.lastvisit.title') }} {{ formattedDate }}
                </p>
                <div class="user">
                    <p class="active-patient">Active Patient</p>
                    <span class="visit-date">Last visit: Dec 15, 2023</span>
                </div>
            </div>
        </div>
        <div v-clickaway="closeDropdown" class="refer-class">
            <common-button
                :label="$t('modals.refer_patient.buttons.refer')"
                size="medium"
                icon="add-user"
                type="refer-patient"
                class="refer-patient-btn"
                :isClicked="isDropdownOpen"
                @click="toggleDropdown"
            ></common-button>
            <common-dropdown
                :isOpen="isDropdownOpen"
                class="refer-patient-dropdown"
                :hasArrow="false"
                bgColor="gray"
                arrowPositionY="top"
                arrowPositionX="left"
            >
                <common-dropdown-items :menuItems="menuItems" @item-clicked="itemClicked"></common-dropdown-items>
            </common-dropdown>
        </div>
        <direct-referral
            v-if="isDirectReferralModalOpen"
            :activePracticeType="practiceType"
            :insurancePlanId="physician?.mcard?.primaryInsuranceTypeId || patientInsuranceId"
            @location-selected="toggleModal"
            @close="toggleModal"
        ></direct-referral>
    </div>
</template>
<script>
    import dayjs from 'dayjs'
    import PhysicianMixin from 'mixins/PhysicianMixin'
    import DirectReferral from 'components/page-elements/referrals/DirectReferral'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'

    export default {
        mixins: [PhysicianMixin, ScreenSizeDetectionMixin],
        components: {
            DirectReferral,
        },
        props: {
            physician: { type: Object, required: true },
        },
        data() {
            return {
                isDropdownOpen: false,
                isDirectReferralModalOpen: false,
                selectedInsurance: [],
                PracticeTypeEnum: PracticeTypeEnum,
                practiceType: null,
                patientInsuranceId: 246, //self pay
            }
        },
        computed: {
            menuItems() {
                return [
                    {
                        name: this.$t('layouts.app.topnav.direct_referral'),
                        description: this.$t('layouts.app.topnav.direct_referral_description'),
                        icon: 'direct-referral',
                        subItems: [
                            {
                                name: this.$t('layouts.app.topnav.direct_physican'),
                                payload: { directReferral: true, practiceType: PracticeTypeEnum.PHYSICIAN },
                            },
                            {
                                name: this.$t('layouts.app.topnav.direct_ancillary'),
                                payload: { directReferral: true, practiceType: PracticeTypeEnum.ANCILLARY },
                            },
                        ],
                    },
                    {
                        name: this.$t('layouts.app.topnav.match_referral'),
                        description: this.$t('layouts.app.topnav.match_referral_description'),
                        icon: 'match-referral',
                        subItems: [
                            {
                                name: this.$t('layouts.app.topnav.match_physican'),
                                payload: {
                                    matchReferral: true,
                                    practiceType: PracticeTypeEnum.PHYSICIAN,
                                    patientId: this.$route.params.id,
                                },
                            },
                            {
                                name: this.$t('layouts.app.topnav.match_ancillary'),
                                payload: { matchReferral: true, practiceType: PracticeTypeEnum.ANCILLARY },
                            },
                        ],
                    },
                    {
                        name: this.$t('layouts.app.topnav.eprescribe'),
                        description: this.$t('layouts.app.topnav.eprescribe_description'),
                        icon: 'eprescribe',
                        payload: { eprescribe: true, route: 'referrals.eprescribe' },
                        hidden: this.$auth.isUserPracticeOther(),
                        disabled: false,
                    },
                ]
            },
            avatarSize() {
                switch (this.size) {
                    case 'medium':
                        return 'user-context'
                    default:
                        return 'member-switch'
                }
            },
            formattedDate() {
                return this.physician.lastReferralCreatedDateTime
                    ? dayjs(this.physician.lastReferralCreatedDateTime).format('MMMM D, YYYY')
                    : undefined
            },
            getArrowPositionX() {
                return this.isMobile ? 'left' : 'right'
            },
        },
        methods: {
            toggleDropdown() {
                this.isDropdownOpen = !this.isDropdownOpen
            },
            closeDropdown() {
                this.isDropdownOpen = false
            },
            toggleModal(practiceType) {
                this.practiceType = practiceType
                this.isDirectReferralModalOpen = !this.isDirectReferralModalOpen
            },
            changeInsurance(event) {
                this.selectedInsurance = event
            },
            openMatchReferral(practiceType, patientId = null, insurancePlanId) {
                const query = { practiceType: practiceType, insurancePlanId: insurancePlanId }
                if (patientId) {
                    query.patientId = patientId
                }
                this.$router.push({ name: `pool.new`, query })
            },

            itemClicked(payload) {
                if (payload.directReferral) {
                    this.toggleModal(payload.practiceType)
                }
                if (payload.matchReferral) {
                    this.openMatchReferral(
                        payload.practiceType,
                        payload.patientId,
                        this.physician?.mcard?.primaryInsuranceTypeId || this.patientInsuranceId,
                    )
                }

                if (payload.eprescribe) {
                    this.$router.push({ name: payload.route })
                }
            },
        },

        watch: {
            $route() {
                if (this.$route.query?.openModal?.length) {
                    this.toggleModal(this.$route.query.openModal)
                    const query = Object.assign({}, this.$route.query)
                    delete query.openModal
                    this.$router.replace({ query })
                }
            },
        },
    }
</script>
<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .user {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
    }
    h1.main-head {
        color: oklch(0.145 0 0);
        font-size: 25px;
        font-weight: 500;
        margin-bottom: 12px;
    }
    .profile {
        display: flex;
        gap: 20%;
        width: 100%;
        background-color: #fff;
        border-top: 1px solid #ddd;
        border-bottom: 1px solid #ddd;
        border-radius: 2px;
        padding: 10px;

        @include breakpoint(laptop) {
            gap: 10%;
            @include breakpoint(tablet) {
                gap: 0;
                display: block;
            }
        }
    }
    p.active-patient {
        background: oklch(0.95 0.0058 264.53);
        padding: 2px 7px;
        border-radius: 8px;
        color: #030213;
        font-size: 14px;
        font-weight: 500;
    }
    span.visit-date {
        color: oklch(0.551 0.027 264.364);
        font-size: 14px;
        margin-left: 12px;
        margin: 12px;

        @media (max-width: 768px) {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
        }
    }

    .refer-class {
        align-self: center;
    }

    .refer-patient-btn {
        position: relative;
    }

    .refer-patient-dropdown {
        @include breakpoint(tablet-small) {
            min-width: unset;
            left: 0;

            .app-user-dropdown__menu {
                @include breakpoint(smartphone-large) {
                    max-width: 100px;
                }
                &--item__submenu--items {
                    button {
                        height: 50px;
                    }
                }

                &--item__submenu {
                    @include breakpoint(smartphone-large) {
                        max-width: 200px;
                    }
                }
            }
        }
    }
</style>
